/**
 * CommandPalette — global ⌘K / Ctrl+K navigation search.
 *
 * Renders a Linear/Notion-style fuzzy-search dialog over all navigation
 * destinations. Solves the "menu is overwhelming, tedious to scroll"
 * feedback by giving power users an instant-jump shortcut and casual
 * users a single discoverable surface to find any feature.
 *
 * Triggers:
 *   - Keyboard: ⌘K (mac) / Ctrl+K (win/linux)
 *   - Button:   the search icon in QMSLayout header / mobile nav
 *
 * Routes are flat by design: items are namespaced by group label so the
 * user sees "Operations · CAPA" rather than just "CAPA", which addresses
 * the "grouped by function" half of the tester's feedback.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { DialogTitle } from "@radix-ui/react-dialog";
import { History, Star } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./ui/command";
import { useCommandPalette } from "../contexts/CommandPaletteContext";

// PR_RUN_032 — localStorage key for the user's last-N visited routes,
// shown above the search results when the palette opens empty.
const RECENTS_LS_KEY = "qms.commandPalette.recents.v1";
const RECENTS_MAX = 10;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const _hydrateRecents = () => {
  try {
    const raw = window.localStorage.getItem(RECENTS_LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, RECENTS_MAX) : [];
  } catch (_) {
    return [];
  }
};

const _persistRecents = (recents) => {
  try {
    window.localStorage.setItem(RECENTS_LS_KEY, JSON.stringify(recents.slice(0, RECENTS_MAX)));
  } catch (_) {
    /* private browsing — silently degrade */
  }
};

const CommandPalette = ({ navGroups = [] }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // PR_RUN_035 — open state now lives in the CommandPaletteContext so the
  // launcher button (topbar) can call openPalette() instead of poking a
  // window-global. Local `setOpen` is preserved here as a shadowed alias
  // for minimal-diff readability below.
  const { open, setOpen, togglePalette } = useCommandPalette();
  const [query, setQuery] = useState("");
  const [recents, setRecents] = useState(_hydrateRecents);
  const [pinned, setPinned] = useState([]);

  // PR_RUN_032 — flatten nav items once so recents/pinned lookups can hydrate
  // the same icon + group label the search results carry.
  const itemIndex = useMemo(() => {
    const idx = {};
    for (const group of navGroups) {
      for (const item of group.items || []) {
        idx[item.path] = { ...item, groupLabel: group.label };
      }
    }
    return idx;
  }, [navGroups]);

  // Track route changes → push to recents (deduped, length-capped).
  useEffect(() => {
    const path = location.pathname;
    if (!path || !itemIndex[path]) return;  // ignore non-nav routes (e.g. /verify/...)
    setRecents((prev) => {
      const next = [path, ...prev.filter((p) => p !== path)].slice(0, RECENTS_MAX);
      _persistRecents(next);
      return next;
    });
  }, [location.pathname, itemIndex]);

  // Best-effort pinned-favourites fetch on first open.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/favourites`, { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setPinned(Array.isArray(data.favourites) ? data.favourites : []);
      } catch (_) {
        /* unauthenticated or network — pinned stays empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        togglePalette();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [togglePalette]);

  // PR_RUN_035 — `window.__openCommandPalette` global removed. Topbar
  // launcher now calls `useCommandPalette().openPalette()` directly.

  const handleSelect = useCallback(
    (path) => {
      setOpen(false);
      navigate(path);
    },
    [navigate],
  );

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      data-testid="command-palette"
      // shadcn CommandDialog passes through additional Dialog props to the
      // underlying Radix Dialog.Content.
    >
      <VisuallyHidden>
        <DialogTitle>Command Palette — jump to anywhere in CHARSTAN QMS</DialogTitle>
      </VisuallyHidden>
      <CommandInput
        placeholder="Jump to anywhere…  (try: capa, audit, wind, certificates)"
        data-testid="command-palette-input"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList data-testid="command-palette-list">
        <CommandEmpty data-testid="command-palette-empty">
          No matches. Try a feature name or module.
        </CommandEmpty>

        {/* PR_RUN_032 — Recents + Pinned, shown when the query is empty. */}
        {query.trim() === "" && recents.length > 0 && (
          <>
            <CommandGroup heading="Recents" data-testid="command-palette-recents">
              {recents
                .map((path) => itemIndex[path])
                .filter(Boolean)
                .map((item) => {
                  const Icon = item.icon || History;
                  return (
                    <CommandItem
                      key={`recent-${item.path}`}
                      value={`recent ${item.label} ${item.path}`}
                      onSelect={() => handleSelect(item.path)}
                      data-testid={`command-palette-recent-${item.path.slice(1).replace(/\//g, "-")}`}
                    >
                      <Icon size={14} className="mr-2 text-slate-400" />
                      <span className="flex-1">{item.label}</span>
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">
                        {item.groupLabel}
                      </span>
                    </CommandItem>
                  );
                })}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {query.trim() === "" && pinned.length > 0 && (
          <>
            <CommandGroup heading="Pinned" data-testid="command-palette-pinned">
              {pinned.map((fav) => {
                const meta = itemIndex[fav.path] || {};
                const Icon = meta.icon || Star;
                return (
                  <CommandItem
                    key={`pin-${fav.path}`}
                    value={`pinned ${fav.label} ${fav.path}`}
                    onSelect={() => handleSelect(fav.path)}
                    data-testid={`command-palette-pinned-${fav.path.replace(/\//g, "-")}`}
                  >
                    <Icon size={14} className="mr-2 text-[#C9A84C]" />
                    <span className="flex-1">{fav.label}</span>
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">
                      {meta.groupLabel || "Favourite"}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {navGroups.map((group, idx) => (
          <React.Fragment key={group.id}>
            {idx > 0 && <CommandSeparator />}
            <CommandGroup heading={group.label}>
              {group.items.map((item) => {
                const Icon = item.icon;
                // Searchable keywords: include both label and group so
                // typing "operations" surfaces the whole Operations bundle.
                const keywords = [group.label, item.label].join(" ");
                return (
                  <CommandItem
                    key={item.path}
                    value={`${keywords} ${item.path}`}
                    onSelect={() => handleSelect(item.path)}
                    data-testid={`command-palette-item-${item.path.slice(1).replace(/\//g, "-")}`}
                  >
                    {Icon ? <Icon size={14} className="mr-2 text-slate-400" /> : null}
                    <span className="flex-1">{item.label}</span>
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">
                      {group.label}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </React.Fragment>
        ))}
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
